import { AloftPrice, ManualAdjustments } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectDateOfSaleOrListToSaleAdjustments } from '../calculatedMarketAdjustmentSelectors/selectDateOfSaleOrListToSaleAdjustment.ts';
import { selectCompSequence } from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { priceJSONToPrice } from '../utils/priceJSONToPrice.ts';
import { selectADUAdjs } from './selectADUAdj.ts';
import { selectAreaAdjs } from './selectAreaAdj.ts';
import { selectAtticAdjs } from './selectAtticAdjustment.ts';
import { selectBarnAdjs } from './selectBarnAdj.ts';
import { selectBelowGradeFullBathsAdjs } from './selectBelowGradeFullBathsAdj.ts';
import { selectBelowGradeHalfBathsAdjs } from './selectBelowGradeHalfBathsAdj.ts';
import { selectCarportAdjs } from './selectCarportAdj.ts';
import { selectConditionAdjs } from './selectConditionAdj.ts';
import { selectDeckAdjs } from './selectDeckAdj.ts';
import { selectExternalObsolescenceAdjs } from './selectExternalObsolescenceAdj.ts';
import { selectFinishedBasementAreaAdjs } from './selectFinishedBasementAreaAdj.ts';
import { selectFireplaceAdjs } from './selectFireplaceAdjustment.ts';
import { selectFullBathsAdjs } from './selectFullBathsAdj.ts';
import { selectFunctionalUtilityAdjs } from './selectFunctionalUtilityAdj.ts';
import { selectGarageAdjs } from './selectGarageAdj.ts';
import { selectHalfBathsAdjs } from './selectHalfBathsAdj.ts';
import { selectManualCostAdjs } from './selectManualCostAdj.ts';
import { selectNumberOfUnitsAdjs } from './selectNumberOfUnitsAdj.ts';
import { selectPatioAdjs } from './selectPatioAdj.ts';
import { selectPoolAdjs } from './selectPoolAdj.ts';
import { selectPorchAdjs } from './selectPorchAdj.ts';
import { selectQualityAdjs } from './selectQualityAdj.ts';
import { selectShedAdjs } from './selectShedAdj.ts';
import { selectSiteAdjs } from './selectSiteAdj.ts';
import { selectSpaAdjs } from './selectSpaAdj.ts';
import { selectTotalBasementAreaAdjs } from './selectTotalBasementAreaAdj.ts';
import { selectWorkshopAdjs } from './selectWorkshopAdj.ts';

export type Adjustments = {
  condition?: AloftPrice;
  quality?: AloftPrice;
  site?: AloftPrice;

  gla?: AloftPrice;
  numberOfUnits?: AloftPrice;

  /** @deprecated - Use bathAboveGrade instead */
  fullBaths?: AloftPrice;
  /** @deprecated - Use bathAboveGrade instead */
  halfBaths?: AloftPrice;
  bathAboveGrade?: AloftPrice;

  /** @deprecated - Use bathBelowGrade instead */
  belowGradeFullBaths?: AloftPrice;
  /** @deprecated - Use bathBelowGrade instead */
  belowGradeHalfBaths?: AloftPrice;
  bathBelowGrade?: AloftPrice;

  bedrooms?: AloftPrice;
  /** @deprecated - Use garageCarport instead */
  garage?: AloftPrice;
  /** @deprecated - Use garageCarport instead */
  carport?: AloftPrice;
  garageCarport?: AloftPrice;

  /** @deprecated - Use basement instead */
  finishedBasementArea?: AloftPrice;
  /** @deprecated - Use basement instead */
  totalBasementArea?: AloftPrice;
  basement?: AloftPrice;

  /** @deprecated - Use poolSpa instead */
  pool?: AloftPrice;
  /** @deprecated - Use poolSpa instead */
  spa?: AloftPrice;
  poolSpa?: AloftPrice;

  /** @deprecated - Use porchPatioDeck instead */
  porch?: AloftPrice;
  /** @deprecated - Use porchPatioDeck instead */
  patio?: AloftPrice;
  /** @deprecated - Use porchPatioDeck instead */
  deck?: AloftPrice;
  porchPatioDeck?: AloftPrice;

  adu?: AloftPrice;
  attic?: AloftPrice;
  barn?: AloftPrice;
  fireplace?: AloftPrice;
  manualCost?: AloftPrice;
  shed?: AloftPrice;
  workshop?: AloftPrice;

  functionalUtility?: AloftPrice;
  externalObsolescence?: AloftPrice;

  // Market Adjustments
  dateOfSale?: AloftPrice;
  termsOfSale?: AloftPrice;
  concessions?: AloftPrice;
};

const getAdjustments = (
  barn?: AloftPrice,
  carport?: AloftPrice,
  condition?: AloftPrice,
  porch?: AloftPrice,
  patio?: AloftPrice,
  deck?: AloftPrice,
  finishedBasementArea?: AloftPrice,
  fullBaths?: AloftPrice,
  garage?: AloftPrice,
  halfBaths?: AloftPrice,
  belowGradeFullBaths?: AloftPrice,
  belowGradeHalfBaths?: AloftPrice,
  pool?: AloftPrice,
  quality?: AloftPrice,
  area?: AloftPrice,
  numberOfUnits?: AloftPrice,
  shed?: AloftPrice,
  site?: AloftPrice,
  spa?: AloftPrice,
  totalBasementArea?: AloftPrice,
  workshop?: AloftPrice,
  attic?: AloftPrice,
  fireplace?: AloftPrice,
  manualCost?: AloftPrice,
  adu?: AloftPrice,
  functionalUtility?: AloftPrice,
  externalObsolescence?: AloftPrice,
  dateOfSale?: AloftPrice,
  manualAdjustments?: ManualAdjustments,
): Adjustments => {
  const bathAboveGrade = AloftPrice.addWithNulls(fullBaths, halfBaths) ?? undefined;
  const basement = AloftPrice.addWithNulls(finishedBasementArea, totalBasementArea) ?? undefined;
  const garageCarport = AloftPrice.addWithNulls(garage, carport) ?? undefined;
  const porchPatioDeck = AloftPrice.addWithNulls(deck, porch, patio) ?? undefined;
  const poolSpa = AloftPrice.addWithNulls(pool, spa) ?? undefined;
  const bathBelowGrade =
    AloftPrice.addWithNulls(belowGradeFullBaths, belowGradeHalfBaths) ?? undefined;

  return {
    condition,
    quality,
    site,

    gla: area,
    numberOfUnits,

    fullBaths,
    halfBaths,
    bathAboveGrade,

    belowGradeFullBaths,
    belowGradeHalfBaths,
    bathBelowGrade,

    garage,
    carport,
    garageCarport,

    finishedBasementArea,
    totalBasementArea,
    basement,

    pool,
    spa,
    poolSpa,

    porch,
    patio,
    deck,
    porchPatioDeck,

    adu,
    attic,
    barn,
    bedrooms: undefined,
    fireplace,
    manualCost,
    shed,
    workshop,

    functionalUtility,
    externalObsolescence,

    dateOfSale,
    termsOfSale: priceJSONToPrice(manualAdjustments?.termsOfSale),
    concessions: priceJSONToPrice(manualAdjustments?.concessions),
  };
};

export const selectAdjustments = createSelector(
  [
    selectBarnAdjs,
    selectCarportAdjs,
    selectConditionAdjs,
    selectPorchAdjs,
    selectPatioAdjs,
    selectDeckAdjs,
    selectFinishedBasementAreaAdjs,
    selectFullBathsAdjs,
    selectGarageAdjs,
    selectHalfBathsAdjs,
    selectBelowGradeFullBathsAdjs,
    selectBelowGradeHalfBathsAdjs,
    selectPoolAdjs,
    selectQualityAdjs,
    selectAreaAdjs,
    selectNumberOfUnitsAdjs,
    selectShedAdjs,
    selectSiteAdjs,
    selectSpaAdjs,
    selectTotalBasementAreaAdjs,
    selectWorkshopAdjs,
    selectAtticAdjs,
    selectFireplaceAdjs,
    selectManualCostAdjs,
    selectADUAdjs,
    selectFunctionalUtilityAdjs,
    selectExternalObsolescenceAdjs,
    selectDateOfSaleOrListToSaleAdjustments,
    selectManualAdjustmentsEntities,
    selectCompSequence,
  ],
  (
    barnAdjs,
    carportAdjs,
    conditionAdjs,
    porchAdjs,
    patioAdjs,
    deckAdjs,
    finishedBasementAreaAdjs,
    fullBathsAdjs,
    garageAdjs,
    halfBathsAdjs,
    belowGradeFullBathAdjs,
    belowGradeHalfBathAdjs,
    poolAdjs,
    qualityAdjs,
    areaAdjs,
    numberOfUnitAdjs,
    shedAdjs,
    siteAdjs,
    spaAdjs,
    totalBasementAreaAdjs,
    workshopAdjs,
    atticAdjs,
    fireplaceAdjs,
    manualCostAdjs,
    aduAdjs,
    functionalUtilityAdjs,
    externalObsolescenceAdjs,
    dateOfSaleAdjs,
    manualAdjustments,
    compSequence,
  ) =>
    compSequence.reduce((acc: Record<string, Adjustments>, propID) => {
      const adjs = getAdjustments(
        barnAdjs[propID],
        carportAdjs[propID],
        conditionAdjs[propID],
        porchAdjs[propID],
        patioAdjs[propID],
        deckAdjs[propID],
        finishedBasementAreaAdjs[propID],
        fullBathsAdjs[propID],
        garageAdjs[propID],
        halfBathsAdjs[propID],
        belowGradeFullBathAdjs[propID],
        belowGradeHalfBathAdjs[propID],
        poolAdjs[propID],
        qualityAdjs[propID],
        areaAdjs[propID],
        numberOfUnitAdjs[propID],
        shedAdjs[propID],
        siteAdjs[propID],
        spaAdjs[propID],
        totalBasementAreaAdjs[propID],
        workshopAdjs[propID],
        atticAdjs[propID],
        fireplaceAdjs[propID],
        manualCostAdjs[propID],
        aduAdjs[propID],
        functionalUtilityAdjs[propID],
        externalObsolescenceAdjs[propID],
        dateOfSaleAdjs[propID],
        manualAdjustments[propID],
      );

      if (adjs) acc[propID] = adjs;
      return acc;
    }, {}),
);
